import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { fetchData } from "../../api/api";
import { useAppConfig } from "../../context/AppConfigContext";
import { useAuth } from "../../context/AuthContext";
import BuyButton from "../PaymentComponent/BuyButton";
import styles from "./Premium.module.css";

const REFERRAL_TIERS = [
  { key: "silver", title: "Premium Silver", target: 7 },
  { key: "gold", title: "Premium Gold", target: 15 },
  { key: "allinclusive", title: "All Inclusive", target: 30 },
];

const CASHBACK_COUNTDOWN_DAYS = 7;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const getNextReferralTier = (count) =>
  REFERRAL_TIERS.find((tier) => count < tier.target) || null;

const getRegistrationTimestamp = (userInfo) => {
  const rawDate =
    userInfo?.createdAt ||
    userInfo?.created_at ||
    userInfo?.registrationDate ||
    userInfo?.registeredAt ||
    userInfo?.registered_at ||
    null;

  if (!rawDate) return null;
  const timestamp = new Date(rawDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const getCashbackCountdownText = (lang, registrationTimestamp, nowTimestamp) => {
  if (!Number.isFinite(registrationTimestamp)) return null;

  const deadline = registrationTimestamp + CASHBACK_COUNTDOWN_DAYS * DAY_MS;
  const diff = Math.max(deadline - nowTimestamp, 0);

  if (diff <= 0) {
    if (lang === "ru") return "время акции истекло";
    if (lang === "uk") return "час акції завершився";
    if (lang === "de") return "Aktion abgelaufen";
    return "offer expired";
  }

  const days = Math.floor(diff / DAY_MS);
  const hours = Math.floor((diff % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((diff % HOUR_MS) / MINUTE_MS);

  if (lang === "ru") return `осталось: ${days}д ${hours}ч ${minutes}м`;
  if (lang === "uk") return `залишилось: ${days}д ${hours}г ${minutes}хв`;
  if (lang === "de") return `noch: ${days}T ${hours}Std ${minutes}Min`;
  return `${days}d ${hours}h ${minutes}m left`;
};

const Premium = ({ userId, premiumLevel = 0, onClick }) => {
  const { t, isBrowser, baseLanguage, apiBaseUrl } = useAppConfig();
  const { isGuest, userInfo } = useAuth();
  const [tariffConfig, setTariffConfig] = useState({});
  const [texts, setTexts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralCount, setReferralCount] = useState(0);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [nowTimestamp, setNowTimestamp] = useState(Date.now());

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true);
      try {
        const url = `${apiBaseUrl}/api/premium/config?lang=${baseLanguage}`;
        const response = await fetch(url);
        const result = await response.json();

        if (result.success && result.data) {
          setTariffConfig(result.data.tariffs);
          setTexts(result.data.texts);
        } else {
          throw new Error("Invalid response");
        }
      } catch (err) {
        setError("Error loading Premium data.");
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [apiBaseUrl, baseLanguage, isNative]);

  useEffect(() => {
    if (isGuest || !userId) {
      setReferralCount(0);
      return;
    }

    const loadReferralStats = async () => {
      try {
        const res = await fetchData("/api/referrals", "POST", { userId });
        setReferralCount(
          Number.isFinite(res.referralCount)
            ? res.referralCount
            : Array.isArray(res.referrals)
              ? res.referrals.length
              : 0,
        );
      } catch (err) {
        setReferralCount(0);
      }
    };

    loadReferralStats();
  }, [isGuest, userId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p className={styles.error}>{error}</p>;
  if (!texts) return null;

  const labels = texts.labels || {};
  const registrationTimestamp = getRegistrationTimestamp(userInfo);
  const nextTier = getNextReferralTier(referralCount);
  const progressTarget = nextTier?.target || referralCount || 1;
  const progressPercent = Math.min((referralCount / progressTarget) * 100, 100);

  const freePlan = {
    id: "free",
    key: "free",
    title: texts.free?.title || "Free",
    badge: texts.free?.badge,
    priceLabel: texts.free?.price || "0 EUR",
    priceOldLabel: null,
    practice: texts.free?.practice || "100 sec",
    lessons: texts.free?.lessons || "50%",
    referral: texts.free?.referral || "10%",
    duration: texts.free?.duration || "Always on",
    savings: null,
    inlineSavings: null,
    showSavingsNote: false,
    cashback: null,
    showTopCashback: false,
    cashbackCountdown: null,
    details: texts.free?.details,
    pdf: texts.free?.pdf || null,
    isFree: true,
  };

  const paidPlans = [1, 2, 3, 6]
    .map((id) => {
      const tariff = tariffConfig[id];
      if (!tariff) {
        return null;
      }

      const key = tariff.googlename;
      const copy = texts[key] || {};
      const isValuePlan = key === "gold" || key === "platinum";
      const isAllInclusive = key === "allinclusive";
      const priceNote = copy.priceNote || `EUR ${Number(tariff.euroPrice).toFixed(2)}`;
      const priceBonus = copy.priceBonus || null;
      const monthsCount = Math.max(1, Math.round((tariff.duration || 30) / 30));
      const baseMonthTariff = tariffConfig[1];
      const monthlyBaseValue = Number(baseMonthTariff?.euroPrice);
      const calculatedMonthlyPrice = Number.isFinite(monthlyBaseValue)
        ? `EUR ${(monthlyBaseValue * monthsCount).toFixed(2)}`
        : null;
      const monthlyFullPrice = copy.pricePerMonth || calculatedMonthlyPrice;
      const savingsValue = copy.savings || (tariff.savings ? `${tariff.savings}%` : null);

      return {
        id,
        key,
        title: copy.title || tariff.name,
        badge: copy.badge,
        priceLabel: isAllInclusive ? priceBonus || priceNote : priceNote,
        priceOldLabel: isValuePlan ? monthlyFullPrice : (isAllInclusive && priceBonus ? priceNote : null),
        practice: copy.practice || `${tariff.practiceMultiplier}x`,
        lessons: null,
        referral: copy.referral || `${tariff.referralRate}%`,
        duration:
          copy.duration ||
          (tariff.type === "lifetime"
            ? "Lifetime"
            : `${Math.round((tariff.duration || 30) / 30)} months`),
        savings: savingsValue,
        inlineSavings: isValuePlan ? savingsValue : null,
        showSavingsNote: !isValuePlan && Boolean(savingsValue),
        cashback: copy.cashback || null,
        showTopCashback: isAllInclusive && Boolean(copy.cashback),
        cashbackCountdown: isAllInclusive
          ? getCashbackCountdownText(baseLanguage, registrationTimestamp, nowTimestamp)
          : null,
        details: copy.details || tariff.details || tariff.description,
        pdf: copy.pdf || tariff.pdf || null,
        tariff,
      };
    })
    .filter(Boolean);

  const plans = [freePlan, ...paidPlans];

  return (
    <section className={styles.premiumBlock}>
      <div className={styles.hero}>
        <div className={styles.heroText}>
          <p className={styles.eyebrow}>{texts.compareTitle || "Plan comparison"}</p>
          <h2 className={styles.title}>{texts.title}</h2>
          <p className={styles.description}>{texts.description}</p>
        </div>

        <div className={styles.progressCard}>
          <p className={styles.progressLabel}>{texts.levelInfo?.howTo}</p>
          <h3 className={styles.progressTitle}>
            {nextTier ? texts[nextTier.key]?.title || nextTier.title : "All levels unlocked"}
          </h3>
          <div className={styles.progressBar} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className={styles.progressMeta}>
            {nextTier
              ? `${referralCount} / ${nextTier.target}`
              : `${referralCount} / ${referralCount || 30}`}
          </p>
          <button className={styles.referralButton} onClick={onClick}>
            {texts.levelInfo?.footer}
          </button>
        </div>
      </div>

      <div className={styles.compareHeader}>
        <h3>{texts.compareTitle || "Plan comparison"}</h3>
        <p>{texts.compareHint || "Tap ? to view details"}</p>
      </div>

      <div className={styles.planGrid}>
        {plans.map((plan) => {
          const isActive = Number(premiumLevel) === Number(plan.id);
          const isExpanded = expandedPlan === plan.id;
          const hasDetails = Boolean(plan.details);

          return (
            <article
              key={plan.id}
              className={`${styles.planCard} ${isActive ? styles.activePlan : ""} ${styles[`plan_${plan.key}`] || ""}`}
            >
              <div className={styles.cardTop}>
                <div>
                  {plan.badge ? <span className={styles.badge}>{plan.badge}</span> : null}
                  <h4 className={styles.planTitle}>{plan.title}</h4>
                </div>
                {hasDetails ? (
                  <button
                    className={styles.infoButton}
                    onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                    aria-label={`More about ${plan.title}`}
                  >
                    ?
                  </button>
                ) : null}
              </div>

              <div className={styles.priceLine}>
                {plan.priceOldLabel ? (
                  <span className={styles.priceOldValue}>{plan.priceOldLabel}</span>
                ) : null}
                <span className={styles.priceValue}>{plan.priceLabel}</span>
                {plan.inlineSavings ? (
                  <span className={styles.savingsPill}>
                    {labels.savings || "Savings"}: {plan.inlineSavings}
                  </span>
                ) : null}
                {isActive ? (
                  <span className={styles.activePill}>{t.premium?.bought || "active"}</span>
                ) : null}
              </div>

              {plan.showTopCashback ? (
                <p className={styles.cashbackLine}>
                  <span className={styles.cashbackLabel}>{labels.cashback || "Bonus"}:</span>
                  <span>{plan.cashback}</span>
                  {plan.cashbackCountdown ? (
                    <span className={styles.cashbackCountdown}>{plan.cashbackCountdown}</span>
                  ) : null}
                </p>
              ) : null}

              <dl className={styles.featureList}>
                <div>
                  <dt>{labels.practice || "Practice"}</dt>
                  <dd>{plan.practice}</dd>
                </div>
                {plan.lessons ? (
                  <div>
                    <dt>{labels.lessons || "Lessons"}</dt>
                    <dd>{plan.lessons}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>{labels.referral || "Referral"}</dt>
                  <dd>{plan.referral}</dd>
                </div>
                <div>
                  <dt>{labels.duration || "Duration"}</dt>
                  <dd>{plan.duration}</dd>
                </div>
              </dl>

              {plan.showSavingsNote ? (
                <p className={styles.planNote}>
                  {labels.savings || "Savings"}: {plan.savings}
                </p>
              ) : null}

              {plan.cashback && !plan.showTopCashback ? (
                <p className={styles.planNote}>
                  {labels.cashback || "Bonus"}: {plan.cashback}
                </p>
              ) : null}

              {plan.pdf ? (
                <p className={styles.pdfNote}>
                  <span className={styles.pdfLabel}>{labels.pdf || "PDF"}</span>
                  <span>{plan.pdf}</span>
                </p>
              ) : null}

              {isExpanded && hasDetails ? (
                <div className={styles.detailsPanel}>
                  {plan.details ? <p className={styles.planDetails}>{plan.details}</p> : null}
                </div>
              ) : null}

              {!plan.isFree ? (
                <BuyButton
                  tariff={plan.id}
                  tariffConfig={plan.tariff}
                  isNative={isNative}
                  isBrowser={isBrowser && !isGuest}
                />
              ) : (
                <div className={styles.freePill}>Free access</div>
              )}
            </article>
          );
        })}
      </div>

      <p className={styles.paymentNote}>{texts.payment_note}</p>
    </section>
  );
};

export default Premium;
